// ============================================================================
// GST ENGAGEMENT VIEW MODAL — G-1 slice 1
// ============================================================================
// Read-only detail for one GST engagement: the header context plus the GSTIN
// registrations in scope for that financial year.
//
// The list grid shows only a COUNT of registrations, because the list endpoint
// deliberately doesn't hydrate them. This modal is where that count becomes
// legible — it fetches the one row by id, which is the only place the
// per-registration filing frequency, HSN level and return set are visible.

import React, { useCallback, useEffect, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import notificationService from 'services/notificationService';
import { gstEngagementService } from 'api/services/gstEngagementService';
import type { GstEngagement, GstEngagementRegistration } from 'api/types/gstEngagement.types';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

interface Props {
  isOpen: boolean;
  /** The row clicked in the grid — used for the header while the detail loads. */
  summary: GstEngagement | null;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (recordId: string) => void;
}

const fyLabel = (y: number) => `FY ${y}-${String((y + 1) % 100).padStart(2, '0')}`;

/** "Apr 2026" — a cadence election is always a quarter start, so the day is noise. */
const formatQuarter = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

/**
 * One frequency is only the truth when there was one election. Say so plainly
 * rather than showing whichever happened to be first — a client who moved to
 * QRMP in October is not "MONTHLY" for the year.
 */
const describeCadence = (reg: GstEngagementRegistration): string => {
  const options = reg.filingOptions ?? [];
  if (options.length <= 1) return reg.filingFrequency;
  return `${options.map((o) => o.filingFrequency).join(' → ')} (changed mid-year)`;
};

const Field: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
      {label}
    </div>
    <div style={{ fontSize: '0.875rem', color: 'var(--text-primary)', marginTop: 2 }}>
      {value ?? '—'}
    </div>
  </div>
);

const GstEngagementViewModal: React.FC<Props> = ({
  isOpen, summary, onClose, tabId, moduleName, onRestoreView,
}) => {
  const { minimize } = useModalMinimize();

  const [detail, setDetail] = useState<GstEngagement | null>(null);
  const [loading, setLoading] = useState(false);
  const detailRef = useRef<GstEngagement | null>(null);
  detailRef.current = detail;

  useEffect(() => {
    if (!isOpen || !summary?.id) return;
    let cancelled = false;
    setLoading(true);
    gstEngagementService
      .getById(summary.id)
      .then((d) => { if (!cancelled) setDetail(d); })
      .catch(() => {
        if (cancelled) return;
        notificationService.toast.error('Could not load the GST engagement detail.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, summary?.id]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !summary) return;
    const savedDetail = detailRef.current;
    const label = summary.clientLegalName || summary.clientName || 'GST';
    minimize({
      id: `${tabId}-view-modal-${summary.id}`,
      title: `View - ${label}`,
      moduleName: moduleName ?? 'GST Engagements',
      tabId,
      // A read-only view can never hold unsaved work, so it never blocks a
      // tab close or a logout.
      isDirty: false,
      onRestore: () => {
        if (savedDetail) setDetail(savedDetail);
        onRestoreView?.(summary.id);
      },
    });
    onClose();
  }, [tabId, moduleName, summary, minimize, onClose, onRestoreView]);

  if (!isOpen || !summary) return null;

  const row = detail ?? summary;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            {row.clientLegalName || row.clientName || 'GST Engagement'} · {fyLabel(row.financialYear)}
          </h2>
          {/* Both buttons share one flex child — .modal-header spreads its
              direct children with space-between. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose} title="Close">
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <div className="form-grid-3" style={{ marginBottom: '1rem' }}>
            <Field label="Engagement" value={row.engagementCode || '—'} />
            <Field label="Engagement Type" value={row.engagementTypeName || '—'} />
            <Field label="Data Source" value={row.dataSourceMode || '—'} />
          </div>

          <div style={{ fontWeight: 600, fontSize: '0.875rem', marginBottom: 8 }}>
            GSTIN registrations in scope
          </div>

          {loading && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              Loading registrations…
            </div>
          )}

          {!loading && (detail?.registrations ?? []).length === 0 && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)' }}>
              No registrations in scope.
            </div>
          )}

          {(detail?.registrations ?? []).map((reg) => (
            <div
              key={reg.id}
              style={{
                border: '1px solid var(--border-color)',
                borderRadius: 6,
                padding: '0.625rem',
                marginBottom: '0.5rem',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                <span style={{ fontFamily: 'monospace', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {reg.gstin}
                </span>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                  state {reg.stateCode}
                  {reg.jurisdiction ? ` · ${reg.jurisdiction}` : ''}
                </span>
                {reg.scopeStatus === 'WITHDRAWN' && (
                  <span className="badge badge-inactive">Withdrawn</span>
                )}
              </div>
              <div className="form-grid-4">
                <Field label="Trade name" value={reg.tradeName || '—'} />
                <Field label="Filing frequency" value={describeCadence(reg)} />
                <Field label="HSN digits" value={reg.requiredHsnDigits} />
                <Field label="Returns" value={(reg.returnTypes ?? []).join(' · ') || '—'} />
              </div>

              {/* Only rendered when the client switched cadence mid-year, which
                  is the whole point of ADR-0019: showing a single frequency
                  would be wrong for part of the year, and silently so. */}
              {(reg.filingOptions ?? []).length > 1 && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed var(--border-color)' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 4 }}>
                    Filing frequency changed during the year
                  </div>
                  {reg.filingOptions.map((opt) => (
                    <div key={opt.id} style={{ fontSize: '0.8125rem', color: 'var(--text-primary)' }}>
                      <strong>{opt.filingFrequency}</strong> from {formatQuarter(opt.effectiveFromQuarter)}
                      <span style={{ color: 'var(--text-secondary)' }}>
                        {' '}— {opt.returnTypes.join(' · ')}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}

          {row.filingNotes && (
            <div style={{ marginTop: '1rem' }}>
              <Field label="Filing notes" value={row.filingNotes} />
            </div>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </>
  );
};

export default GstEngagementViewModal;
